import { Box, styled, Tooltip, Typography } from '@mui/material';
import { ICellRendererParams } from 'ag-grid-community';
import { FC } from 'react';

const defaultFormatter = (value: unknown) => value?.toString();

export const ComparableCell: FC<IComparableCellRendererParams> = ({
    value,
    formatValue = defaultFormatter,
    hideTooltip = false,
}) => {
    /**
     * Value can be either a primitive value (string or number)
     * or it can be a tuple / an array in which case we assume current value is first
     * and predicted value is second
     * @see {@link mergeTableOverviewRowValuesToTuples}
     * @see {@link mergeTableDetailsRowValuesToTuples}
     */
    const current = Array.isArray(value) ? value[0] : value;
    const predicted = Array.isArray(value) ? value[1] : undefined;

    const currentFormatted = formatValue(current)?.toString();
    const predictedFormatted = predicted ? formatValue(predicted)?.toString() : predicted?.toString();

    const tooltipText = predictedFormatted ? `(${currentFormatted}) ${predictedFormatted}` : currentFormatted;

    return (
        <Tooltip title={hideTooltip ? false : tooltipText} placement="top" enterDelay={500} enterNextDelay={500}>
            <Wrapper>
                <CurrentValue hasPredicted={typeof predictedFormatted !== 'undefined'}>{currentFormatted}</CurrentValue>
                {typeof predictedFormatted !== 'undefined' && <ComparedValue>{predictedFormatted}</ComparedValue>}
            </Wrapper>
        </Tooltip>
    );
};

const Wrapper = styled(Box)(() => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
}));

const CurrentValue = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'hasPredicted',
})<{ hasPredicted?: boolean }>(({ hasPredicted }) => ({
    fontSize: hasPredicted ? '12px' : '14px', // FYI: Theme font overrides cause too small
    '&:before': {
        content: hasPredicted ? "'('" : '""',
    },
    '&:after': {
        content: hasPredicted ? "')'" : '""',
    },
}));

const ComparedValue = styled(Typography)(({ theme }) => ({
    fontSize: '14px', // FYI: Theme font overrides cause too small
    color: theme.palette.info.main,
    marginLeft: theme.spacing(1),
}));

interface IComparableCellRendererParams extends ICellRendererParams {
    hideTooltip?: boolean;
}
